import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import Button from '../../../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactRef,
} from '../../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import useCreateNewSlide from '../../../../../../service/hooks/useCreateNewSlide';
import {TEMPLATE} from '../../../../../../service/template/types';
import {createSlideCopyAction, updateSlideWithoutElementsAction} from '../../../../../../store/actions/slide';
import {
    selectedSlideHiddenSelector,
    selectedSlideOnAutoFavoriteSelector,
    selectedSlideSelector,
} from '../../../../../../store/selectors/slide.selectors';

type MenuBarSlideDropdownProps = {
    slideMenuItemRef: React.RefObject<DropdownMenuCompactRef>;
    menuBarOpen: boolean;
    onHoverEnter: () => void;
    onMenuClose: () => void;
    onOpenMenuBar: () => void;
    onOpenImportModal: () => void;
};

function MenuBarSlideDropdown({
    slideMenuItemRef,
    menuBarOpen,
    onHoverEnter,
    onMenuClose,
    onOpenMenuBar,
    onOpenImportModal,
}: MenuBarSlideDropdownProps): JSX.Element {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {createNewSlide} = useCreateNewSlide();
    const slideHidden = useSelector(selectedSlideHiddenSelector);
    const slideOnAutoFavorite = useSelector(selectedSlideOnAutoFavoriteSelector);
    const selectedSlide = useSelector(selectedSlideSelector);

    const slideMenuItems = useMemo(
        () => [
            [
                {
                    label: t('menuBar.slideMenu.new'),
                    icon: 'solar:add-square-linear',
                    items: [
                        [
                            {
                                label: t('presEditPage.subNavBar.slide.blank'),
                                icon: 'solar:stop-linear',
                                onClick: () => createNewSlide(TEMPLATE.BLANK),
                                dataCy: TEMPLATE.BLANK.toString(),
                            },
                            {
                                label: t('presEditPage.subNavBar.slide.titleSubtitle'),
                                icon: 'solar:align-left-linear',
                                onClick: () => createNewSlide(TEMPLATE.TITLE_SUBTITLE),
                                dataCy: TEMPLATE.TITLE_SUBTITLE.toString(),
                            },
                            {
                                label: t('presEditPage.subNavBar.slide.image'),
                                icon: 'solar:gallery-minimalistic-linear',
                                onClick: () => createNewSlide(TEMPLATE.IMAGE),
                                dataCy: TEMPLATE.IMAGE.toString(),
                            },
                            {
                                label: t('presEditPage.subNavBar.slide.titleTextImage'),
                                icon: 'solar:widget-4-linear',
                                onClick: () => createNewSlide(TEMPLATE.TITLE_TEXT_IMAGE),
                                dataCy: TEMPLATE.TITLE_TEXT_IMAGE.toString(),
                            },
                        ],
                        [
                            {
                                label: t('presEditPage.importSlide'),
                                icon: 'solar:upload-minimalistic-linear',
                                onClick: onOpenImportModal,
                                dataCy: 'import-slide',
                            },
                        ],
                    ],
                },
                {
                    label: t('menuBar.slideMenu.duplicate'),
                    icon: 'solar:copy-linear',
                    onClick: () => {
                        dispatch(createSlideCopyAction(selectedSlide, 1));
                    },
                },
                {
                    label: slideHidden ? t('menuBar.slideMenu.show') : t('menuBar.slideMenu.hide'),
                    icon: slideHidden ? 'solar:eye-linear' : 'solar:eye-closed-linear',
                    onClick: () => {
                        dispatch(
                            updateSlideWithoutElementsAction({
                                ...selectedSlide,
                                hidden: !selectedSlide.hidden,
                            })
                        );
                    },
                },
                {
                    label: slideOnAutoFavorite ? t('menuBar.slideMenu.removeBookmark') : t('menuBar.slideMenu.bookmark'),
                    icon: slideOnAutoFavorite ? 'fluent:star-off-12-filled' : 'solar:star-linear',
                    onClick: () => {
                        dispatch(
                            updateSlideWithoutElementsAction({
                                ...selectedSlide,
                                onAutoFavorite: !selectedSlide.onAutoFavorite,
                            })
                        );
                    },
                },
            ],
        ],
        [t, slideHidden, slideOnAutoFavorite, createNewSlide, dispatch, selectedSlide, onOpenImportModal]
    );

    return (
        <DropdownMenuCompact
            ref={slideMenuItemRef}
            openOnMouseEnter={menuBarOpen}
            onHoverEnter={onHoverEnter}
            onMenuClose={onMenuClose}
            items={slideMenuItems}
        >
            <Button size="2xs" disableHover square variant="ghost" className="c-menuBar__button" onClick={onOpenMenuBar}>
                {t('menuBar.slide')}
            </Button>
        </DropdownMenuCompact>
    );
}

export default React.memo(MenuBarSlideDropdown);
